import { logClientError } from '@utils/server-log';
import { Injectable, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import {
  AUTH_FAILURE_COPY,
  AUTH_FAILURE_STORAGE_KEY,
  AUTH_RELOAD_ATTEMPTS_KEY,
  AuthBootstrapError,
  AuthFailureDetail,
  AuthFailureReason,
} from '@models/auth-failure';
import { USER_PERMISSIONS_STORAGE_KEY } from '@models/permissions';
import { LoadingService } from '@services/loading/loading.service';
import { SharedService } from '@services/shared/shared.service';
import { extractMessageFromUnknown, extractStatusCode } from '@utils/backend-error.utils';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Observable, TimeoutError, of } from 'rxjs';

export interface AuthFailureOptions {
  /** Exceção original (gRPC/HTTP/JS). */
  error?: unknown;
  /** Dados extras para o log (rota, feature exigida, permissões atuais...). */
  context?: Record<string, unknown>;
}

/**
 * Ponto único de saída para qualquer falha de autenticação/autorização.
 *
 * Garante o fluxo de boas práticas: loga o diagnóstico, desloga o usuário e
 * limpa todo o cache (tokens, cookies, storages), avisa por toast e devolve o
 * usuário ao /login com o motivo visível em tela.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthFailureService {
  private readonly router = inject(Router);
  private readonly shared = inject(SharedService);
  private readonly cookieService = inject(CookieService);
  private readonly loadingService = inject(LoadingService);
  private readonly toastr = inject(ToastrService);

  /** Janela e limite para detecção de loop de redirecionamento entre guards. */
  private static readonly LOOP_WINDOW_MS = 5_000;
  private static readonly LOOP_THRESHOLD = 4;

  /** Evita logout/toast duplicados quando vários guards falham na mesma navegação. */
  private failureInFlight = false;

  private guardRedirects: number[] = [];

  // ─── Falha terminal ─────────────────────────────────────────

  /** Falha em fluxo imperativo (AuthGuard): limpa tudo e navega para /login. */
  fail(reason: AuthFailureReason, options: AuthFailureOptions = {}): Observable<false> {
    const detail = this.prepareFailure(reason, options);

    if (detail) {
      this.router.navigate(['/login'], {
        queryParams: { error: detail.reason },
        replaceUrl: true,
      });
    }

    return of(false as const);
  }

  /** Falha dentro de um guard que responde com UrlTree (FeatureGuard). */
  failUrlTree(reason: AuthFailureReason, options: AuthFailureOptions = {}): UrlTree {
    const detail = this.prepareFailure(reason, options);

    return this.router.createUrlTree(['/login'], {
      queryParams: { error: detail?.reason ?? reason },
    });
  }

  /**
   * Executa log + limpeza + toast uma única vez por ciclo de falha.
   * Retorna `null` quando já existe uma falha em andamento.
   */
  private prepareFailure(
    reason: AuthFailureReason,
    options: AuthFailureOptions
  ): AuthFailureDetail | null {
    if (this.failureInFlight) {
      logClientError(`[AuthFailure] Falha "${reason}" ignorada: logout já em andamento.`);
      return null;
    }
    this.failureInFlight = true;

    const detail = this.buildDetail(reason, options);

    this.logFailure(detail, options);
    this.purgeSession();
    this.persistFailure(detail);
    this.notifyUser(detail);
    this.loadingService.loading$.next(false);
    this.guardRedirects = [];

    return detail;
  }

  private buildDetail(reason: AuthFailureReason, options: AuthFailureOptions): AuthFailureDetail {
    const copy = AUTH_FAILURE_COPY[reason] ?? AUTH_FAILURE_COPY.unknown_error;
    const error = options.error;

    return {
      reason,
      title: copy.title,
      message: copy.message,
      technicalMessage: error ? extractMessageFromUnknown(error) || String(error) : undefined,
      code: error ? extractStatusCode(error) ?? undefined : undefined,
      url:
        typeof window !== 'undefined'
          ? window.location.pathname + window.location.search
          : undefined,
      at: new Date().toISOString(),
    };
  }

  private logFailure(detail: AuthFailureDetail, options: AuthFailureOptions): void {
    logClientError(`[AuthFailure] ${detail.reason}`, {
      title: detail.title,
      message: detail.message,
      reason: detail.reason,
      code: detail.code ?? '(sem código)',
      technicalMessage: detail.technicalMessage ?? '(sem mensagem do backend)',
      url: detail.url,
      at: detail.at,
      user: this.shared.user?.email ?? this.shared.user?.id ?? '(desconhecido)',
      userFeatures: this.shared.userFeatures,
      menuFeatures: this.shared.menuFeatures,
      ...options.context,
      error: options.error,
    });
  }

  /** Limpa tokens, cookies, storages e estado em memória. */
  private purgeSession(): void {
    try {
      this.shared.logoutUser(false);
    } catch (error) {
      logClientError('[AuthFailure] Erro ao limpar sessão do usuário:', error);
    }

    try {
      this.cookieService.delete('user', '/');
      this.cookieService.delete('accessToken', '/');
      this.cookieService.delete('refreshToken', '/');
      this.cookieService.delete('redirectUrl', '/');

      sessionStorage.removeItem(USER_PERMISSIONS_STORAGE_KEY);
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem(AUTH_RELOAD_ATTEMPTS_KEY);
      localStorage.removeItem('oauth_state');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('idToken');
    } catch (error) {
      logClientError('[AuthFailure] Erro ao limpar cookies/storage:', error);
    }
  }

  private persistFailure(detail: AuthFailureDetail): void {
    try {
      sessionStorage.setItem(AUTH_FAILURE_STORAGE_KEY, JSON.stringify(detail));
    } catch (error) {
      logClientError('[AuthFailure] Não foi possível persistir o detalhe da falha:', error);
    }
  }

  private notifyUser(detail: AuthFailureDetail): void {
    const suffix = detail.technicalMessage ? ` (${detail.technicalMessage})` : '';

    try {
      this.toastr.error(`${detail.message}${suffix}`, detail.title, {
        positionClass: 'toast-bottom-center',
        closeButton: true,
        timeOut: 9000,
      });
    } catch (error) {
      logClientError('[AuthFailure] Toast indisponível:', error, detail);
    }
  }

  // ─── Consumo na tela de login ───────────────────────────────

  /** Lê e remove o último detalhe de falha (para exibição no /login). */
  consumeFailure(): AuthFailureDetail | null {
    try {
      const raw = sessionStorage.getItem(AUTH_FAILURE_STORAGE_KEY);
      sessionStorage.removeItem(AUTH_FAILURE_STORAGE_KEY);

      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as AuthFailureDetail;
    } catch (error) {
      logClientError('[AuthFailure] Detalhe de falha inválido no storage:', error);
      sessionStorage.removeItem(AUTH_FAILURE_STORAGE_KEY);
      return null;
    }
  }

  /** Libera o serviço para tratar novas falhas (chamado ao chegar no /login). */
  release(): void {
    this.failureInFlight = false;
    this.guardRedirects = [];
  }

  get isFailing(): boolean {
    return this.failureInFlight;
  }

  // ─── Proteção contra loop de redirecionamento ───────────────

  /**
   * Registra um redirecionamento feito por guard.
   * @returns `false` quando o limite de redirects na janela foi estourado.
   */
  trackGuardRedirect(from: string, to: string): boolean {
    const now = Date.now();
    this.guardRedirects = this.guardRedirects.filter(
      (timestamp) => now - timestamp < AuthFailureService.LOOP_WINDOW_MS
    );
    this.guardRedirects.push(now);

    if (this.guardRedirects.length >= AuthFailureService.LOOP_THRESHOLD) {
      logClientError('[AuthFailure] Loop de redirecionamento detectado', {
        from,
        to,
        redirectsNaJanela: this.guardRedirects.length,
        janelaMs: AuthFailureService.LOOP_WINDOW_MS,
      });
      return false;
    }

    return true;
  }

  /** Navegação bem-sucedida: zera o contador de redirects. */
  resetRedirectTracking(): void {
    this.guardRedirects = [];
  }

  // ─── Helpers ────────────────────────────────────────────────

  /** Traduz uma exceção do bootstrap para o motivo correspondente. */
  static resolveReason(
    error: unknown,
    fallback: AuthFailureReason = 'unknown_error'
  ): AuthFailureReason {
    if (error instanceof AuthBootstrapError) {
      return error.reason;
    }
    if (error instanceof TimeoutError) {
      return 'bootstrap_timeout';
    }

    const code = extractStatusCode(error);
    if (code === 16 || code === 401) {
      return 'session_expired';
    }

    return fallback;
  }

  /** Desembrulha o erro original de um AuthBootstrapError. */
  static unwrap(error: unknown): unknown {
    return error instanceof AuthBootstrapError ? error.cause ?? error : error;
  }

  /** Contexto extra anexado ao AuthBootstrapError, quando houver. */
  static contextOf(error: unknown): Record<string, unknown> | undefined {
    return error instanceof AuthBootstrapError ? error.context : undefined;
  }
}
